import { useState } from "react";
import { toast } from "sonner";
import { submitReferral } from "../lib/api";
import { Loader2, School } from "lucide-react";

const inputCls = "w-full rounded-md border border-black/15 bg-white px-4 py-3 outline-none focus:border-[#6B21A8]";

export default function PartnerReferrals() {
  const [f, setF] = useState({
    referrer_name: "", referrer_role: "", referrer_email: "", referrer_org: "",
    student_first_name: "", student_last_name: "", student_age: "", student_grade: "",
    student_school: "", referral_reason: "", parent_contacted: false, urgency: "normal",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitReferral({ ...f, student_age: Number(f.student_age) });
      toast.success("Referral received. A program director will follow up within 3 business days.");
      setF({ referrer_name: "", referrer_role: "", referrer_email: "", referrer_org: "", student_first_name: "", student_last_name: "", student_age: "", student_grade: "", student_school: "", referral_reason: "", parent_contacted: false, urgency: "normal" });
    } catch { toast.error("Submission failed."); } finally { setLoading(false); }
  };

  return (
    <>
      <section className="bg-[#FDFBF7] border-b border-black/10">
        <div className="container-x py-20 md:py-24">
          <div className="flex items-center gap-3 mb-3">
            <School className="h-5 w-5 text-[#6B21A8]" />
            <span className="overline text-[#6B21A8]">Partner Portal · School Referrals</span>
          </div>
          <h1 className="mt-4 font-display text-5xl md:text-6xl font-bold tracking-tight leading-none">Refer a young man.<br /><span className="italic">We'll take it from here.</span></h1>
          <p className="mt-6 max-w-2xl text-black/70 text-lg">
            For school counselors, principals, court officers, and community partners referring boys ages 10–17 into our mentoring program.
          </p>
        </div>
      </section>

      <section className="bg-white">
        <div className="container-x py-16 max-w-3xl">
          <form onSubmit={submit} className="space-y-6" data-testid="ref-form">
            <fieldset className="space-y-4">
              <legend className="overline text-[#6B21A8] mb-2">Referring partner</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block"><span className="block text-sm font-medium mb-2">Your name *</span><input required className={inputCls} value={f.referrer_name} onChange={set("referrer_name")} data-testid="ref-name" /></label>
                <label className="block"><span className="block text-sm font-medium mb-2">Your role *</span><input required className={inputCls} placeholder="Counselor, Principal, Court Officer…" value={f.referrer_role} onChange={set("referrer_role")} data-testid="ref-role" /></label>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block"><span className="block text-sm font-medium mb-2">Email *</span><input required type="email" className={inputCls} value={f.referrer_email} onChange={set("referrer_email")} data-testid="ref-email" /></label>
                <label className="block"><span className="block text-sm font-medium mb-2">Organization *</span><input required className={inputCls} value={f.referrer_org} onChange={set("referrer_org")} data-testid="ref-org" /></label>
              </div>
            </fieldset>

            <fieldset className="space-y-4 pt-6 border-t border-black/10">
              <legend className="overline text-[#6B21A8] mb-2">Student</legend>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block"><span className="block text-sm font-medium mb-2">First name *</span><input required className={inputCls} value={f.student_first_name} onChange={set("student_first_name")} data-testid="ref-sf" /></label>
                <label className="block"><span className="block text-sm font-medium mb-2">Last name *</span><input required className={inputCls} value={f.student_last_name} onChange={set("student_last_name")} data-testid="ref-sl" /></label>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <label className="block"><span className="block text-sm font-medium mb-2">Age *</span><input required type="number" min="10" max="17" className={inputCls} value={f.student_age} onChange={set("student_age")} data-testid="ref-age" /></label>
                <label className="block"><span className="block text-sm font-medium mb-2">Grade *</span><input required className={inputCls} value={f.student_grade} onChange={set("student_grade")} data-testid="ref-grade" /></label>
                <label className="block"><span className="block text-sm font-medium mb-2">School</span><input className={inputCls} value={f.student_school} onChange={set("student_school")} data-testid="ref-school" /></label>
              </div>
              <label className="block"><span className="block text-sm font-medium mb-2">Why are you referring this student? *</span>
                <textarea required rows="4" className={inputCls} value={f.referral_reason} onChange={set("referral_reason")} data-testid="ref-reason" placeholder="Behavior, academics, family situation, growth areas…" />
              </label>
              <label className="flex items-center gap-3 pt-2">
                <input type="checkbox" checked={f.parent_contacted} onChange={set("parent_contacted")} data-testid="ref-parent" className="h-4 w-4 rounded border-black/30 text-[#6B21A8]" />
                <span className="text-sm text-black/70">Parent/guardian is aware of this referral</span>
              </label>
              <div>
                <p className="text-sm font-medium mb-2">Urgency</p>
                <div className="flex gap-2">
                  {["normal", "urgent"].map((v) => (
                    <button key={v} type="button" onClick={() => setF({ ...f, urgency: v })}
                      className={`rounded-full border px-4 py-1.5 text-sm capitalize ${f.urgency === v ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/15"}`}
                      data-testid={`ref-urg-${v}`}>{v}</button>
                  ))}
                </div>
              </div>
            </fieldset>

            <button type="submit" disabled={loading} data-testid="ref-submit" className="btn-primary w-full">
              {loading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>) : "Submit referral"}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
